<template>
  <Teleport to="body">
    <div v-if="open" class="hist-mask" @click.self="close">
      <div class="hist" role="dialog" aria-modal="true">
        <header class="hist__head">
          <div>
            <p class="hist__sub">历史</p>
            <h2>{{ title }}</h2>
          </div>
          <button type="button" class="hist__close" @click="close">×</button>
        </header>

        <div class="hist__meta">
          <span>{{ kind }}（{{ unit || "—" }}）</span>
          <span>近 48 小时 · 小时步长</span>
        </div>

        <div class="hist__chart">
          <ChartBox :option="option" height="100%" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, watch } from "vue";
import ChartBox from "@/components/ChartBox.vue";
import { buildMockHistory } from "@/utils/mockHistory";
import { tooltipDark } from "@/utils/chartTheme";

const props = defineProps({
  open: { type: Boolean, default: false },
  fields: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["close"]);

const series = computed(() => buildMockHistory(props.fields, 48, 60));
const title = computed(() => props.fields?.名称 || "监测站点");
const kind = computed(() => series.value.kind);
const unit = computed(() => series.value.unit);

const option = computed(() => {
  const { times, values, unit: u, kind: k } = series.value;
  return {
    color: ["#3de7ff"],
    tooltip: {
      ...tooltipDark,
      trigger: "axis",
      valueFormatter: (v) => (u ? `${v} ${u}` : String(v)),
    },
    grid: { left: 48, right: 24, top: 36, bottom: 48 },
    xAxis: {
      type: "category",
      data: times,
      boundaryGap: false,
      axisLabel: {
        color: "rgba(216,246,255,0.55)",
        fontSize: 11,
        hideOverlap: true,
      },
      axisLine: { lineStyle: { color: "rgba(61,231,255,0.25)" } },
    },
    yAxis: {
      type: "value",
      name: u ? `${k}(${u})` : k,
      nameTextStyle: { color: "rgba(216,246,255,0.55)", fontSize: 11 },
      axisLabel: { color: "rgba(216,246,255,0.55)", fontSize: 11 },
      splitLine: { lineStyle: { color: "rgba(61,231,255,0.08)" } },
    },
    dataZoom: [
      { type: "inside", start: 50, end: 100 },
      {
        type: "slider",
        height: 18,
        bottom: 8,
        borderColor: "rgba(61,231,255,0.25)",
        fillerColor: "rgba(61,231,255,0.12)",
        handleStyle: { color: "#3de7ff" },
        textStyle: { color: "rgba(216,246,255,0.55)" },
      },
    ],
    series: [
      {
        name: k,
        type: "line",
        data: values,
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 2, color: "#3de7ff" },
        areaStyle: {
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(61,231,255,0.35)" },
              { offset: 1, color: "rgba(61,231,255,0.02)" },
            ],
          },
        },
      },
    ],
  };
});

function close() {
  emit("close");
}

function onKey(e) {
  if (e.key === "Escape" && props.open) {
    e.stopPropagation();
    close();
  }
}

watch(
  () => props.open,
  (v) => {
    document.body.style.overflow = v ? "hidden" : "";
  }
);

onMounted(() => window.addEventListener("keydown", onKey, true));
onUnmounted(() => {
  window.removeEventListener("keydown", onKey, true);
  document.body.style.overflow = "";
});
</script>

<style scoped>
.hist-mask {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: grid;
  place-items: center;
  padding: 24px;
  background: rgba(1, 6, 12, 0.72);
  backdrop-filter: blur(6px);
}

.hist {
  width: min(1100px, 96vw);
  height: min(720px, 88vh);
  display: flex;
  flex-direction: column;
  background: linear-gradient(
    165deg,
    rgba(6, 22, 38, 0.98) 0%,
    rgba(3, 12, 22, 0.98) 100%
  );
  border: 1px solid rgba(61, 231, 255, 0.35);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55);
  overflow: hidden;
}

.hist__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid rgba(61, 231, 255, 0.16);
}

.hist__sub {
  font-size: 11px;
  letter-spacing: 0.14em;
  color: var(--cyan, #3de7ff);
  margin-bottom: 4px;
}

.hist__head h2 {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
}

.hist__close {
  width: 32px;
  height: 32px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: transparent;
  color: rgba(216, 246, 255, 0.75);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  font-family: inherit;
}

.hist__close:hover {
  color: #fff;
  border-color: rgba(61, 231, 255, 0.45);
}

.hist__meta {
  display: flex;
  gap: 16px;
  padding: 10px 20px 0;
  font-size: 12px;
  color: rgba(216, 246, 255, 0.55);
}

.hist__chart {
  flex: 1;
  min-height: 0;
  padding: 8px 12px 16px;
}

.hist__chart :deep(.chart-box) {
  height: 100% !important;
  min-height: 360px;
}
</style>
